import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { adminCreate, adminListProps, adminUpdate } from "../lib/api";
import { useAuth } from "../auth";
import { toast } from "sonner";
import { ArrowLeft, Save, Phone } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import MediaUploader from "../components/MediaUploader";

const EMPTY = {
  title: "", transaction_type: "satilik", property_type: "daire", price: "",
  city: "", district: "", neighborhood: "", address: "",
  room_count: "", bathroom_count: 1, net_area: 0, gross_area: 0,
  floor: "", total_floors: "", building_age: "", heating_type: "",
  parking: false, elevator: false, balcony: false, furnished: false,
  description: "", features: [], images: [], video_url: "", external_link: "", agent_phone: "", featured: false, status: "aktif",
};

export default function AdminPropertyForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const nav = useNavigate();
  const { user } = useAuth();
  const [f, setF] = useState(EMPTY);
  const [featuresText, setFeaturesText] = useState("");

  useEffect(() => {
    if (!isEdit) return;
    adminListProps().then((all) => {
      const p = all.find((x) => x.id === id);
      if (p) { setF({ ...EMPTY, ...p, video_url: p.video_url || "" }); setFeaturesText((p.features || []).join(", ")); }
    });
  }, [id, isEdit]);

  const upd = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const fmtPrice = (v) => {
    const d = String(v ?? "").replace(/\D/g, "");
    return d ? Number(d).toLocaleString("tr-TR") : "";
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      ...f,
      price: Number(f.price) || 0,
      bathroom_count: Number(f.bathroom_count) || 0,
      net_area: Number(f.net_area) || 0,
      gross_area: Number(f.gross_area) || 0,
      features: featuresText.split(",").map((s) => s.trim()).filter(Boolean),
      images: f.images || [],
      video_url: f.video_url || "",
    };
    try {
      if (isEdit) { await adminUpdate(id, payload); toast.success("İlan güncellendi"); }
      else { await adminCreate(payload); toast.success("İlan oluşturuldu"); }
      nav("/admin");
    } catch { toast.error("Kaydedilemedi"); }
  };

  return (
    <div className="min-h-screen bg-slate-100 py-10">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-4"><ArrowLeft className="w-4 h-4" /> Panele dön</Link>
        <div className="bg-white border border-slate-200 rounded-2xl p-8">
          <h1 className="font-display text-3xl text-slate-900 mb-6">{isEdit ? "İlanı Düzenle" : "Yeni İlan"}</h1>
          <form onSubmit={submit} className="space-y-5" data-testid="admin-property-form">
            <div>
              <label className="label-caps block mb-2">Başlık</label>
              <Input required data-testid="pf-title" value={f.title} onChange={(e) => upd("title", e.target.value)} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="label-caps block mb-2">İşlem</label>
                <Select value={f.transaction_type} onValueChange={(v) => upd("transaction_type", v)}>
                  <SelectTrigger data-testid="pf-tx"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="satilik">Satılık</SelectItem>
                    <SelectItem value="kiralik">Kiralık</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="label-caps block mb-2">Tip</label>
                <Select value={f.property_type} onValueChange={(v) => upd("property_type", v)}>
                  <SelectTrigger data-testid="pf-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daire">Daire</SelectItem>
                    <SelectItem value="villa">Villa</SelectItem>
                    <SelectItem value="arsa">Arsa</SelectItem>
                    <SelectItem value="isyeri">İşyeri</SelectItem>
                    <SelectItem value="diger">Diğer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="label-caps block mb-2">Fiyat (₺)</label>
                <Input type="text" inputMode="numeric" required data-testid="pf-price" placeholder="ör. 1.250.000" value={fmtPrice(f.price)} onChange={(e) => upd("price", e.target.value.replace(/\D/g, ""))} />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div><label className="label-caps block mb-2">İl</label><Input required data-testid="pf-city" value={f.city} onChange={(e) => upd("city", e.target.value)} /></div>
              <div><label className="label-caps block mb-2">İlçe</label><Input required data-testid="pf-district" value={f.district} onChange={(e) => upd("district", e.target.value)} /></div>
              <div><label className="label-caps block mb-2">Mahalle</label><Input value={f.neighborhood} onChange={(e) => upd("neighborhood", e.target.value)} /></div>
            </div>

            <div><label className="label-caps block mb-2">Adres</label><Input value={f.address} onChange={(e) => upd("address", e.target.value)} /></div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div><label className="label-caps block mb-2">Oda</label><Input placeholder="3+1" value={f.room_count} onChange={(e) => upd("room_count", e.target.value)} /></div>
              <div><label className="label-caps block mb-2">Banyo</label><Input type="number" value={f.bathroom_count} onChange={(e) => upd("bathroom_count", e.target.value)} /></div>
              <div><label className="label-caps block mb-2">Net m²</label><Input type="number" value={f.net_area} onChange={(e) => upd("net_area", e.target.value)} /></div>
              <div><label className="label-caps block mb-2">Brüt m²</label><Input type="number" value={f.gross_area} onChange={(e) => upd("gross_area", e.target.value)} /></div>
              <div><label className="label-caps block mb-2">Kat</label><Input value={f.floor} onChange={(e) => upd("floor", e.target.value)} /></div>
              <div><label className="label-caps block mb-2">Toplam Kat</label><Input value={f.total_floors} onChange={(e) => upd("total_floors", e.target.value)} /></div>
              <div><label className="label-caps block mb-2">Bina Yaşı</label><Input value={f.building_age} onChange={(e) => upd("building_age", e.target.value)} /></div>
              <div><label className="label-caps block mb-2">Isıtma</label><Input value={f.heating_type} onChange={(e) => upd("heating_type", e.target.value)} /></div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[["parking", "Otopark"], ["elevator", "Asansör"], ["balcony", "Balkon"], ["furnished", "Eşyalı"]].map(([k, label]) => (
                <label key={k} className="flex items-center justify-between bg-slate-50 rounded-lg p-3 border border-slate-100">
                  <span className="text-sm text-slate-700">{label}</span>
                  <Switch checked={!!f[k]} onCheckedChange={(v) => upd(k, v)} />
                </label>
              ))}
            </div>

            <div><label className="label-caps block mb-2">Açıklama</label><Textarea rows={5} value={f.description} onChange={(e) => upd("description", e.target.value)} /></div>
            <div>
              <label className="label-caps block mb-2">Dış Kaynak Linki (Sahibinden, Hepsiemlak vb.)</label>
              <Input data-testid="pf-external-link" type="url" placeholder="https://www.sahibinden.com/ilan/..." value={f.external_link} onChange={(e) => upd("external_link", e.target.value)} />
              <div className="text-xs text-slate-500 mt-1">Bu link ilan detay sayfasında müşterilere "İlanı Kaynakta Görüntüle" butonu olarak gösterilir. Boş bırakabilirsiniz.</div>
            </div>
            <div className="bg-slate-50 border border-slate-100 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <Phone className="w-4 h-4 text-[#C0562F]" />
                <div className="label-caps">İletişim Numarası</div>
              </div>
              <Input
                data-testid="pf-agent-phone"
                type="tel"
                placeholder={user?.phone ? user.phone : "0-5XX-XXXXXXX"}
                value={f.agent_phone}
                onChange={(e) => upd("agent_phone", e.target.value)}
              />
              <div className="text-xs text-slate-500 mt-2">
                Boş bırakılırsa profildeki numaranız (<span className="font-semibold text-slate-800">{user?.phone || "—"}</span>) kullanılır. Müşteriler ilan detayında bu numarayı ve WhatsApp linkini görecek.
              </div>
            </div>
            <div><label className="label-caps block mb-2">Özellikler (virgülle ayırın)</label><Input value={featuresText} onChange={(e) => setFeaturesText(e.target.value)} placeholder="Havuz, Güvenlik, Otopark" /></div>
            <div>
              <label className="label-caps block mb-2">Medya (Fotoğraf & Video)</label>
              <MediaUploader
                images={f.images || []}
                video={f.video_url || ""}
                onChange={({ images, video }) => setF((s) => ({ ...s, images, video_url: video }))}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="flex items-center justify-between bg-slate-50 rounded-lg p-3 border border-slate-100">
                <span className="text-sm text-slate-700">Öne Çıkan İlan</span>
                <Switch checked={!!f.featured} onCheckedChange={(v) => upd("featured", v)} />
              </label>
              <div>
                <label className="label-caps block mb-2">Durum</label>
                <Select value={f.status} onValueChange={(v) => upd("status", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="aktif">Aktif</SelectItem>
                    <SelectItem value="satildi">Satıldı</SelectItem>
                    <SelectItem value="kiralandi">Kiralandı</SelectItem>
                    <SelectItem value="pasif">Pasif</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="pt-4 flex justify-end gap-3">
              <Link to="/admin" className="px-5 py-3 rounded-full border border-slate-200 text-sm">İptal</Link>
              <button type="submit" data-testid="pf-save" className="btn-accent px-6 py-3 rounded-full text-sm font-semibold inline-flex items-center gap-2"><Save className="w-4 h-4" /> Kaydet</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
